import { useState, useEffect } from 'react';
import { QuoteModal } from '../../components/QuoteModal/index';

interface Quote {
  _id: string;
  title: string;
  customer: string;
  totalPrice: number;
  lineItems: any[];
  workflowId?: string;
  approved?: boolean;
  createdAt: string;
  updatedAt: string;
}

export const Quotes = ({ user, onNavigate, selectedQuoteId, onQuoteSelect }: { user: any; onNavigate: (page: string) => void; selectedQuoteId: string | null; onQuoteSelect: (id: string | null) => void }) => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);

  useEffect(() => {
    fetchQuotes();
  }, []);

  useEffect(() => {
    // Auto-open quote modal if selectedQuoteId is provided
    if (selectedQuoteId && quotes.length > 0) {
      const quote = quotes.find(q => q._id === selectedQuoteId);
      if (quote) {
        setSelectedQuote(quote);
        onQuoteSelect(null); // Clear the selection after opening
      }
    }
  }, [selectedQuoteId, quotes]);

  const fetchQuotes = async () => {
    try {
      const response = await fetch('/quotes', {
        headers: {
          'user-id': user.id // Use actual user ID from auth
        }
      });
      
      const result = await response.json();
      if (result.success) {
        setQuotes(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteClick = (quote: Quote) => {
    setSelectedQuote(quote);
  };

  const handleCloseModal = () => {
    setSelectedQuote(null);
  };

  const handleCreateQuote = () => {
    onNavigate('create-quote');
  };

  if (loading) {
    return (
      <div className="page">
        <h1>Your Quotes</h1>
        <p>Loading quotes...</p>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Your Quotes</h1>
      </div>
      
      {quotes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-content">
            <div className="empty-state-icon">🏔️</div>
            <h2>Unlock the full power of Canyon AI</h2>
            <p>Create your first quote to get started with intelligent workflow management</p>
            <button onClick={handleCreateQuote} className="cta-button">
              🚀 Create Your First Quote
            </button>
          </div>
        </div>
      ) : (
        <div className="quotes-grid">
          {quotes.map((quote) => (
            <div 
              key={quote._id} 
              className="quote-card"
              onClick={() => handleQuoteClick(quote)}
            >
              <div className="quote-header">
                <h3>{quote.title}</h3>
                <div className="quote-price">${quote.totalPrice.toLocaleString()}</div>
              </div>
              <div className="quote-meta">
                <p>Created: {new Date(quote.createdAt).toLocaleDateString()}</p>
                <div className="quote-status">
                  {quote.approved ? (
                    <span className="status-badge approved">✅ Approved</span>
                  ) : quote.workflowId ? (
                    <span className="status-badge workflow">🔄 In Progress</span>
                  ) : (
                    <span className="status-badge draft">📝 Draft</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedQuote && (
        <QuoteModal 
          quote={selectedQuote} 
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};